import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, BarChart3, Users, BookOpen, Upload, Banknote, Receipt, ShoppingBag, UserCircle2, Scale, KeyRound } from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Overview", icon: BarChart3, end: true },
  { to: "/admin/authors", label: "Authors", icon: Users },
  { to: "/admin/logins", label: "Author Logins", icon: KeyRound },
  { to: "/admin/titles", label: "Titles", icon: BookOpen },
  { to: "/admin/upload", label: "Upload Reports", icon: Upload },
  { to: "/admin/direct-sales", label: "Direct Sales", icon: ShoppingBag },
  { to: "/admin/royalty", label: "Royalty Reports", icon: Banknote },
  { to: "/admin/payments", label: "Payments", icon: Receipt },
  { to: "/admin/reconciliation", label: "Reconciliation", icon: Scale },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-hh-bg flex">
      <aside className="hidden md:flex flex-col w-64 border-r border-hh-border bg-white sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="px-6 py-7 border-b border-hh-border">
          <NavLink to="/admin" className="flex items-center gap-2.5">
            <img
              src="/healing-house-logo.png"
              alt="Healing House Publishing"
              className="h-11 w-11 object-contain shrink-0"
            />
            <div className="leading-none">
              <div className="font-serif text-lg text-hh-ink">Healing House</div>
              <div className="hh-overline text-[10px] mt-0.5">Admin Console</div>
            </div>
          </NavLink>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {LINKS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive ? "bg-hh-muted text-hh-ink" : "text-hh-ink2 hover:text-hh-ink hover:bg-hh-muted/60"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-hh-border space-y-2">
          <div className="text-xs text-hh-ink2 mb-2">{user?.email}</div>
          <NavLink
            to="/admin/account"
            data-testid="admin-account-link"
            className={({ isActive }) =>
              `flex items-center gap-2 text-sm transition-all ${isActive ? "text-hh-ink" : "text-hh-ink2 hover:text-hh-ink"}`
            }
          >
            <UserCircle2 size={14} strokeWidth={1.5} /> Account & password
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-sm text-hh-ink2 hover:text-hh-ink"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>
      {/* mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-hh-border">
        <div className="flex items-center justify-between px-4 py-3">
          <NavLink to="/admin" className="font-serif text-lg text-hh-ink">Healing House · Admin</NavLink>
          <button onClick={handleLogout} className="text-hh-ink2"><LogOut size={18}/></button>
        </div>
        <nav className="flex items-center gap-1 overflow-x-auto px-4 pb-3">
          {LINKS.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-hh-muted text-hh-ink" : "text-hh-ink2"}`
              }
            >{label}</NavLink>
          ))}
        </nav>
      </div>
      <main className="flex-1 md:p-10 lg:p-12 p-4 pt-28 md:pt-10 max-w-6xl">
        <Outlet />
      </main>
    </div>
  );
}
